import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Plane,
  Filter,
  X,
  LayoutGrid,
  List,
  Columns3,
  Calendar,
  User,
  Clock,
  AlertCircle,
  Pencil,
} from "lucide-react";
import {
  immigrationApi,
  immigrationProgramsApi,
  pipelineStagesApi,
  usersApi,
  PaginatedResponse,
} from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import CreateImmigrationCaseModal from "./CreateImmigrationCaseModal";
import { useMatterInspector } from "../../contexts/MatterInspectorContext";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ImmigrationCase {
  _id: string;
  title: string;
  status: string;
  stage?: string;
  caseType: string;
  jurisdiction: { country: string; region?: string };
  program: { programKey: string; programVersion?: number };
  clientId: { _id: string; displayName: string } | string;
  assigned: {
    responsibleUserId: { _id: string; name: string; email: string } | string;
    assignedUserIds: any[];
  };
  timeline: {
    nextDeadlineAt?: string;
    submittedAt?: string;
    expiryAt?: string;
  };
  matterId?: { _id: string } | string;
  createdAt: string;
}

interface PipelineStage {
  _id: string;
  key: string;
  label: string;
  color: string;
  order: number;
  isDefault?: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  open: "bg-emerald-100 text-emerald-700",
  on_hold: "bg-amber-100 text-amber-700",
  submitted: "bg-blue-100 text-blue-700",
  decision_received: "bg-purple-100 text-purple-700",
  closed: "bg-slate-100 text-slate-600",
};

const formatStatus = (s: string) =>
  s === "open" ? "Active" : s.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const formatCaseType = (ct: string) =>
  ct.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const getClientName = (c: any) => (typeof c === "object" ? c?.displayName : "Unknown");
const getLawyerName = (l: any) => (typeof l === "object" ? l?.name : "Unknown");

const getLawyerInitials = (l: any): string => {
  if (typeof l !== "object" || !l?.name) return "?";
  const parts = l.name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
};

// ─── Kanban Case Card (Draggable) ────────────────────────────────────────────

interface KanbanCardProps {
  caseItem: ImmigrationCase;
  index: number;
  onClick: () => void;
  isOverdue: (d?: string) => boolean;
  isDeadlineSoon: (d?: string) => boolean;
}

const KanbanCard: React.FC<KanbanCardProps> = React.memo(
  ({ caseItem, index, onClick, isOverdue, isDeadlineSoon }) => (
    <Draggable draggableId={caseItem._id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`bg-white rounded-xl border cursor-grab active:cursor-grabbing group ${
            snapshot.isDragging
              ? "shadow-2xl border-brand-light ring-2 ring-brand-light scale-[1.03] rotate-[1.5deg]"
              : "border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
          }`}
          style={{
            ...provided.draggableProps.style,
            transition: snapshot.isDragging
              ? provided.draggableProps.style?.transition
              : `all 0.2s cubic-bezier(0.2, 0, 0, 1), ${provided.draggableProps.style?.transition || ""}`,
          }}
          onClick={onClick}
        >
          <div className="p-3.5">
            {/* Title */}
            <h4 className="text-sm font-semibold text-slate-800 truncate mb-1.5">
              {caseItem.title}
            </h4>

            {/* Client name */}
            <div className="flex items-center gap-2 text-xs text-slate-600 mb-1.5">
              <User className="h-3 w-3 flex-shrink-0" style={{ color: "var(--color-accent)" }} />
              <span className="truncate">{getClientName(caseItem.clientId)}</span>
            </div>

            {/* Program key */}
            <div className="mb-2">
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-mono font-medium bg-blue-50 text-blue-600">
                {caseItem.program.programKey}
              </span>
            </div>

            {/* Deadline */}
            {caseItem.timeline?.nextDeadlineAt && (
              <div
                className={`flex items-center gap-1.5 text-xs mb-2 ${
                  isOverdue(caseItem.timeline.nextDeadlineAt)
                    ? "text-red-600 font-medium"
                    : isDeadlineSoon(caseItem.timeline.nextDeadlineAt)
                    ? "text-amber-600 font-medium"
                    : "text-slate-400"
                }`}
              >
                {isOverdue(caseItem.timeline.nextDeadlineAt) ? (
                  <AlertCircle className="h-3 w-3" />
                ) : (
                  <Clock className="h-3 w-3" />
                )}
                <span>
                  {new Date(caseItem.timeline.nextDeadlineAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            )}

            {/* Bottom row: status badge + lawyer avatar */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  statusColors[caseItem.status] || "bg-slate-100 text-slate-600"
                }`}
              >
                {formatStatus(caseItem.status)}
              </span>
              <div
                className="h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                style={{ backgroundColor: "var(--color-accent)" }}
                title={getLawyerName(caseItem.assigned?.responsibleUserId)}
              >
                {getLawyerInitials(caseItem.assigned?.responsibleUserId)}
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  )
);

KanbanCard.displayName = "KanbanCard";

// ─── Editable Column Header ──────────────────────────────────────────────────

interface EditableHeaderProps {
  stage: PipelineStage;
  count: number;
  onUpdate: (id: string, label: string) => void;
  onDelete: (id: string) => void;
}

const EditableColumnHeader: React.FC<EditableHeaderProps> = React.memo(
  ({ stage, count, onUpdate, onDelete }) => {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(stage.label);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (editing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [editing]);

    const handleSave = () => {
      const trimmed = editValue.trim();
      if (trimmed && trimmed !== stage.label) {
        onUpdate(stage._id, trimmed);
      } else {
        setEditValue(stage.label);
      }
      setEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleSave();
      if (e.key === "Escape") {
        setEditValue(stage.label);
        setEditing(false);
      }
    };

    return (
      <div className="rounded-t-xl px-4 py-3 bg-white flex-shrink-0 border-b-2" style={{ borderColor: stage.color }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
            {editing ? (
              <input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className="text-sm font-semibold text-slate-800 bg-transparent border-b border-brand-accent outline-none flex-1 min-w-0 px-0 py-0"
              />
            ) : (
              <h3
                className="text-sm font-semibold text-slate-700 truncate cursor-pointer group/label flex items-center gap-1"
                onClick={() => setEditing(true)}
              >
                <span className="truncate">{stage.label}</span>
                <Pencil className="h-3 w-3 text-slate-300 opacity-0 group-hover/label:opacity-100 transition-opacity flex-shrink-0" />
              </h3>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            <span
              className="text-xs font-bold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: stage.color + "20", color: stage.color }}
            >
              {count}
            </span>
            {!stage.isDefault && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(stage._id);
                }}
                className="p-0.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                title="Remove column"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
);

EditableColumnHeader.displayName = "EditableColumnHeader";

// ─── Board Column ────────────────────────────────────────────────────────────

interface BoardColumnProps {
  stage: PipelineStage;
  cases: ImmigrationCase[];
  onCardClick: (c: ImmigrationCase) => void;
  onAddCase: () => void;
  onUpdateStage: (id: string, label: string) => void;
  onDeleteStage: (id: string) => void;
  isOverdue: (d?: string) => boolean;
  isDeadlineSoon: (d?: string) => boolean;
}

const BoardColumn: React.FC<BoardColumnProps> = React.memo(
  ({ stage, cases, onCardClick, onAddCase, onUpdateStage, onDeleteStage, isOverdue, isDeadlineSoon }) => (
    <div className="flex-shrink-0 w-[300px] flex flex-col max-h-full">
      <EditableColumnHeader
        stage={stage}
        count={cases.length}
        onUpdate={onUpdateStage}
        onDelete={onDeleteStage}
      />

      <Droppable droppableId={stage.key}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 rounded-b-xl p-2 min-h-[120px] space-y-2 overflow-y-auto transition-colors duration-200 ${
              snapshot.isDraggingOver
                ? "bg-brand-light/70 ring-2 ring-inset ring-brand-light/50"
                : "bg-slate-50/80"
            }`}
          >
            {cases.map((caseItem, index) => (
              <KanbanCard
                key={caseItem._id}
                caseItem={caseItem}
                index={index}
                onClick={() => onCardClick(caseItem)}
                isOverdue={isOverdue}
                isDeadlineSoon={isDeadlineSoon}
              />
            ))}
            {provided.placeholder}
            {cases.length === 0 && !snapshot.isDraggingOver && (
              <div className="flex flex-col items-center justify-center py-6 text-slate-300 text-center">
                <Plane className="h-6 w-6 mb-1 mx-auto" />
                <span className="text-xs">Drag cases here</span>
              </div>
            )}
            <button
              onClick={onAddCase}
              className="flex items-center justify-center gap-1.5 w-full py-2 mt-1 rounded-lg border border-dashed border-slate-200 hover:border-brand-light hover:bg-brand-light/50 transition-all text-slate-400 hover:text-brand-accent text-xs font-medium"
            >
              <Plus className="h-3.5 w-3.5" />
              New Matter
            </button>
          </div>
        )}
      </Droppable>
    </div>
  )
);

BoardColumn.displayName = "BoardColumn";

// ─── Main Page ───────────────────────────────────────────────────────────────

const ImmigrationPage: React.FC = () => {
  const navigate = useNavigate();
  const { openMatterInspector } = useMatterInspector();
  const [cases, setCases] = useState<ImmigrationCase[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [caseTypeFilter, setCaseTypeFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [programFilter, setProgramFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"board" | "cards" | "table">("board");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [programs, setPrograms] = useState<any[]>([]);

  // Pipeline stages for board view
  const [stages, setStages] = useState<PipelineStage[]>([]);

  // Board-specific: all cases (limit 500)
  const [boardCases, setBoardCases] = useState<ImmigrationCase[]>([]);
  const [boardLoading, setBoardLoading] = useState(true);

  // Initial load tracking
  const initialLoadDone = useRef(false);
  const boardInitialLoadDone = useRef(false);

  // Scroll overflow detection
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  // ── Fetch pipeline stages ──────────────────────────────────────────────────

  useEffect(() => {
    pipelineStagesApi.list("immigration").then((res) => {
      const data = res.data?.data || res.data || [];
      setStages(
        [...data].sort((a: PipelineStage, b: PipelineStage) => a.order - b.order)
      );
    }).catch((err) => {
      console.error("Failed to fetch pipeline stages:", err);
    });
  }, []);

  // ── Fetch paginated cases (for cards/table views) ─────────────────────────

  const fetchCases = useCallback(async () => {
    if (!initialLoadDone.current) setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (caseTypeFilter) params.caseType = caseTypeFilter;
      if (countryFilter) params.country = countryFilter;
      if (programFilter) params.programKey = programFilter;

      const res = await immigrationApi.listCases(params);
      const data = res.data as PaginatedResponse<ImmigrationCase>;
      setCases(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch immigration cases:", err);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [pagination.page, pagination.limit, search, statusFilter, caseTypeFilter, countryFilter, programFilter]);

  useEffect(() => {
    if (viewMode !== "board") {
      fetchCases();
    }
  }, [fetchCases, viewMode]);

  // ── Fetch ALL cases for board view ─────────────────────────────────────────

  const fetchBoardCases = useCallback(async () => {
    if (!boardInitialLoadDone.current) setBoardLoading(true);
    try {
      const params: Record<string, any> = { limit: 500 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (caseTypeFilter) params.caseType = caseTypeFilter;
      if (countryFilter) params.country = countryFilter;
      if (programFilter) params.programKey = programFilter;

      const res = await immigrationApi.listCases(params);
      const data = res.data as PaginatedResponse<ImmigrationCase>;
      setBoardCases(data.data);
    } catch (err) {
      console.error("Failed to fetch board cases:", err);
    } finally {
      setBoardLoading(false);
      boardInitialLoadDone.current = true;
    }
  }, [search, statusFilter, caseTypeFilter, countryFilter, programFilter]);

  useEffect(() => {
    if (viewMode === "board") {
      fetchBoardCases();
    }
  }, [fetchBoardCases, viewMode]);

  // ── Fetch programs ─────────────────────────────────────────────────────────

  useEffect(() => {
    immigrationProgramsApi.list({ limit: 100, active: "true" }).then((res) => {
      setPrograms(res.data.data || []);
    }).catch(() => {});
  }, []);

  // ── Debounced search ───────────────────────────────────────────────────────

  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Detect overflow ────────────────────────────────────────────────────────

  useEffect(() => {
    const el = boardScrollRef.current;
    if (!el) return;
    const check = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [stages, boardCases, viewMode]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const isDeadlineSoon = useCallback((dateStr?: string) => {
    if (!dateStr) return false;
    const diff = new Date(dateStr).getTime() - Date.now();
    return diff > 0 && diff < 7 * 86400000;
  }, []);

  const isOverdue = useCallback((dateStr?: string) => {
    if (!dateStr) return false;
    return new Date(dateStr).getTime() < Date.now();
  }, []);

  // ── Group cases by stage for board view ────────────────────────────────────

  const groupedByStage = useMemo(() => {
    const grouped: Record<string, ImmigrationCase[]> = {};
    for (const stage of stages) {
      grouped[stage.key] = [];
    }
    for (const c of boardCases) {
      const key = c.stage || "open";
      if (grouped[key]) {
        grouped[key].push(c);
      } else {
        // Falls into first column if stage key doesn't match
        const firstKey = stages.length > 0 ? stages[0].key : "open";
        if (!grouped[firstKey]) grouped[firstKey] = [];
        grouped[firstKey].push(c);
      }
    }
    return grouped;
  }, [boardCases, stages]);

  // ── Drag and drop handler ──────────────────────────────────────────────────

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { destination, source, draggableId } = result;
      if (!destination) return;
      if (destination.droppableId === source.droppableId && destination.index === source.index) return;

      const newStageKey = destination.droppableId;
      const prevCases = [...boardCases];

      // Optimistic update
      setBoardCases((prev) =>
        prev.map((c) => (c._id === draggableId ? { ...c, stage: newStageKey } : c))
      );

      try {
        await immigrationApi.changeCaseStage(draggableId, newStageKey);
      } catch (err) {
        console.error("Failed to change case stage:", err);
        setBoardCases(prevCases);
      }
    },
    [boardCases]
  );

  // ── Stage management ───────────────────────────────────────────────────────

  const handleUpdateStage = useCallback(async (id: string, label: string) => {
    const prevStages = [...stages];
    setStages((prev) => prev.map((s) => (s._id === id ? { ...s, label } : s)));
    try {
      await pipelineStagesApi.update(id, { label });
    } catch (err) {
      console.error("Failed to update stage:", err);
      setStages(prevStages);
    }
  }, [stages]);

  const handleDeleteStage = useCallback(async (id: string) => {
    const stage = stages.find((s) => s._id === id);
    if (!stage) return;
    if (stage.isDefault) return;

    const casesInStage = groupedByStage[stage.key] || [];
    if (casesInStage.length > 0) {
      const confirmed = window.confirm(
        `This column has ${casesInStage.length} case(s). They will be moved to the first column. Continue?`
      );
      if (!confirmed) return;
    }

    const prevStages = [...stages];
    const prevCases = [...boardCases];

    // Move cases to first stage
    const firstStage = stages.find((s) => s._id !== id);
    if (firstStage && casesInStage.length > 0) {
      setBoardCases((prev) =>
        prev.map((c) => (c.stage === stage.key ? { ...c, stage: firstStage.key } : c))
      );
    }

    setStages((prev) => prev.filter((s) => s._id !== id));

    try {
      await pipelineStagesApi.remove(id);
    } catch (err) {
      console.error("Failed to delete stage:", err);
      setStages(prevStages);
      setBoardCases(prevCases);
    }
  }, [stages, boardCases, groupedByStage]);

  const handleAddColumn = useCallback(async () => {
    const label = window.prompt("New column name:");
    if (!label?.trim()) return;

    try {
      const maxOrder = stages.length > 0 ? Math.max(...stages.map((s) => s.order)) : 0;
      const res = await pipelineStagesApi.create({
        pipelineType: "immigration",
        label: label.trim(),
        color: "#6366f1",
      });
      const newStage = res.data?.data || res.data;
      if (newStage) {
        setStages((prev) => [...prev, { ...newStage, order: newStage.order ?? maxOrder + 1 }]);
      }
    } catch (err) {
      console.error("Failed to create stage:", err);
    }
  }, [stages]);

  // ── Navigation helpers ─────────────────────────────────────────────────────

  const handleCreated = (matterId: string) => {
    setShowCreateModal(false);
    navigate(`/matters/${matterId}`);
  };

  const totalCasesCount = viewMode === "board" ? boardCases.length : pagination.total;

  return (
    <div
      className="flex flex-col"
      style={{ height: viewMode === "board" ? "calc(100vh - 100px)" : "auto" }}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Immigration Cases</h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalCasesCount} case{totalCasesCount !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              onClick={() => setViewMode("board")}
              className={`p-2 transition-colors ${
                viewMode === "board" ? "bg-brand-light text-brand" : "text-slate-400 hover:text-slate-600"
              }`}
              title="Board view"
            >
              <Columns3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("cards")}
              className={`p-2 transition-colors ${
                viewMode === "cards" ? "bg-brand-light text-brand" : "text-slate-400 hover:text-slate-600"
              }`}
              title="Card view"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 transition-colors ${
                viewMode === "table" ? "bg-brand-light text-brand" : "text-slate-400 hover:text-slate-600"
              }`}
              title="Table view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all border shadow-sm hover:shadow-md"
            style={{
              color: "#059669",
              borderColor: "#05966940",
              backgroundColor: "white",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#059669";
              e.currentTarget.style.borderColor = "#059669";
              e.currentTarget.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
              e.currentTarget.style.borderColor = "#05966940";
              e.currentTarget.style.color = "#059669";
            }}
          >
            <Plus className="h-4 w-4" />
            New Immigration Matter
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search cases..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
          />
        </div>
        <SearchableSelect
          options={[
            { value: "open", label: "Active" },
            { value: "on_hold", label: "On Hold" },
            { value: "submitted", label: "Submitted" },
            { value: "decision_received", label: "Decision Received" },
            { value: "closed", label: "Closed" },
          ]}
          value={statusFilter || null}
          onChange={(val) => { setStatusFilter(val || ""); setPagination((prev) => ({ ...prev, page: 1 })); }}
          placeholder="All Statuses"
          clearable
        />
        <SearchableSelect
          options={[
            { value: "temporary_resident", label: "Temporary Resident" },
            { value: "permanent_resident", label: "Permanent Resident" },
            { value: "citizenship", label: "Citizenship" },
            { value: "sponsorship", label: "Sponsorship" },
            { value: "employer_based", label: "Employer Based" },
            { value: "refugee", label: "Refugee" },
            { value: "appeal_review", label: "Appeal/Review" },
            { value: "other", label: "Other" },
          ]}
          value={caseTypeFilter || null}
          onChange={(val) => { setCaseTypeFilter(val || ""); setPagination((prev) => ({ ...prev, page: 1 })); }}
          placeholder="All Case Types"
          clearable
        />
        <SearchableSelect
          options={[
            { value: "CA", label: "Canada" },
            { value: "US", label: "United States" },
            { value: "GB", label: "United Kingdom" },
            { value: "AU", label: "Australia" },
          ]}
          value={countryFilter || null}
          onChange={(val) => { setCountryFilter(val || ""); setPagination((prev) => ({ ...prev, page: 1 })); }}
          placeholder="All Countries"
          clearable
        />
        <SearchableSelect
          options={programs.map((p: any) => ({
            value: p.programKey,
            label: p.name,
          }))}
          value={programFilter || null}
          onChange={(val) => { setProgramFilter(val || ""); setPagination((prev) => ({ ...prev, page: 1 })); }}
          placeholder="All Programs"
          clearable
        />
      </div>

      {/* Content */}
      {viewMode === "board" ? (
        /* ── Board View ─────────────────────────────────────────────────────── */
        boardLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Column count indicator */}
            {hasOverflow && (
              <div className="text-xs text-slate-400 mb-2 flex-shrink-0">
                {stages.length} columns &middot; Scroll to see all &rarr;
              </div>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
              <div
                ref={boardScrollRef}
                className="flex-1 flex gap-4 overflow-x-auto overflow-y-hidden pb-4"
              >
                {stages.map((stage) => (
                  <BoardColumn
                    key={stage._id}
                    stage={stage}
                    cases={groupedByStage[stage.key] || []}
                    onCardClick={(c) => openMatterInspector(typeof c.matterId === "object" ? c.matterId._id : (c.matterId || c._id))}
                    onAddCase={() => setShowCreateModal(true)}
                    onUpdateStage={handleUpdateStage}
                    onDeleteStage={handleDeleteStage}
                    isOverdue={isOverdue}
                    isDeadlineSoon={isDeadlineSoon}
                  />
                ))}

                {/* Add Column placeholder */}
                <div
                  onClick={handleAddColumn}
                  className="flex-shrink-0 w-[300px] min-h-[200px] flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 hover:border-brand-light hover:bg-brand-light/30 transition-all cursor-pointer group"
                >
                  <Plus className="h-8 w-8 text-slate-300 group-hover:text-brand-accent transition-colors mb-2" />
                  <span className="text-sm font-medium text-slate-400 group-hover:text-brand-accent transition-colors">
                    Add Column
                  </span>
                </div>
              </div>
            </DragDropContext>
          </div>
        )
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : cases.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20 text-slate-500">
          <Plane className="h-12 w-12 mb-3 text-slate-300" />
          <p className="font-medium text-lg">No immigration cases yet</p>
          <p className="text-sm mt-1">Create your first case to get started</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-xl text-sm"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Plus className="h-4 w-4" />
            Create First Case
          </button>
        </div>
      ) : viewMode === "cards" ? (
        /* ── Cards View ─────────────────────────────────────────────────────── */
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {cases.map((c) => (
              <div
                key={c._id}
                onClick={() => openMatterInspector(typeof c.matterId === "object" ? c.matterId._id : (c.matterId || c._id))}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer"
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-mono text-slate-400 mb-1">{c.program.programKey}</p>
                      <h3 className="font-semibold text-slate-800 truncate">{c.title}</h3>
                    </div>
                    <span
                      className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium ${
                        statusColors[c.status] || "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {formatStatus(c.status)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <User className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--color-accent)" }} />
                    <span className="truncate">{getClientName(c.clientId)}</span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      {formatCaseType(c.caseType)}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
                      {c.jurisdiction.country}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
                    <Plane className="h-3.5 w-3.5" />
                    <span>{getLawyerName(c.assigned?.responsibleUserId)}</span>
                  </div>

                  {c.timeline?.nextDeadlineAt && (
                    <div className={`flex items-center gap-2 text-xs ${
                      isOverdue(c.timeline.nextDeadlineAt) ? "text-red-600 font-medium" :
                      isDeadlineSoon(c.timeline.nextDeadlineAt) ? "text-amber-600 font-medium" :
                      "text-slate-400"
                    }`}>
                      {isOverdue(c.timeline.nextDeadlineAt) ? (
                        <AlertCircle className="h-3.5 w-3.5" />
                      ) : (
                        <Clock className="h-3.5 w-3.5" />
                      )}
                      <span>
                        Deadline{" "}
                        {new Date(c.timeline.nextDeadlineAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.pages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* ── Table View ─────────────────────────────────────────────────────── */
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Title</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Client</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">Program</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">Next Deadline</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">Lawyer</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr
                    key={c._id}
                    onClick={() => openMatterInspector(typeof c.matterId === "object" ? c.matterId._id : (c.matterId || c._id))}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-slate-800">{c.title}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                      {getClientName(c.clientId)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">
                      {c.program.programKey}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[c.status] || "bg-slate-100 text-slate-600"}`}>
                        {formatStatus(c.status)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 text-sm hidden xl:table-cell ${
                      isOverdue(c.timeline?.nextDeadlineAt) ? "text-red-600 font-medium" :
                      isDeadlineSoon(c.timeline?.nextDeadlineAt) ? "text-amber-600" :
                      "text-slate-500"
                    }`}>
                      {c.timeline?.nextDeadlineAt
                        ? new Date(c.timeline.nextDeadlineAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                        : "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 hidden xl:table-cell">
                      {getLawyerName(c.assigned?.responsibleUserId)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </p>
              <div className="flex gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateImmigrationCaseModal
            onClose={() => setShowCreateModal(false)}
            onCreated={handleCreated}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImmigrationPage;
